import { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Button,
  TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Spinner from '../Spinner';
import intl from 'components/i18n/ReactIntlWrapper';
import ErrorSummary from '../common/ErrorSummary';
import InvoiceUploadDropZone from '../NewInvoice/Upload/InvoiceUploadDropZone';
import { InvoiceProvider, useInvoice } from '../common/InvoiceProvider';
import { useNavigate } from 'react-router';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';

type PersonaOption = { id: number; name: string };

const APInvoiceUploadContent = () => {
  const { input, errors, setErrors } = useInvoice();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const api: Api = useApi();
  const toast = useToastMessageQueue();

  const [supplierOptions, setSupplierOptions] = useState<PersonaOption[]>([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierValue, setSupplierValue] = useState<PersonaOption | null>(null);
  const [supplierInputValue, setSupplierInputValue] = useState('');

  const searchSupplier = useCallback(async (term: string) => {
    setSupplierLoading(true);
    try {
      const res = await api.invoiceApi.apiVversionPersonaEligibleInvoiceFromTypeAheadGet('1', term);
      const results = (res.data?.data?.queryResult ?? [])
        .map((p) => ({ id: p.id, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setSupplierOptions(results);
    } finally {
      setSupplierLoading(false);
    }
  }, [api]);

  const handleUpload = async () => {
    const file = input?.attachments?.[0];
    const newErrors: Record<string, string> = {};

    if (!supplierValue) newErrors['supplier'] = intl.get('accountsPayable.upload.supplierRequired');
    if (!file) newErrors['attachments'] = intl.get('newInvoice.fileRequired') || 'No file selected';

    if (Object.keys(newErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }

    setLoading(true);
    try {
      await api.invoiceApiWrapper.uploadAPInvoice('1', file, supplierValue.id);
      toast.success({ message: intl.get('accountsPayable.upload.success') });
      navigate('/accountspayable');
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors((prev) => ({ ...prev }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 4, px: 2 }}>
      <Card variant="outlined">
        {loading && <Spinner />}
        <CardHeader
          title={
            <Typography color="primary" variant="h2">
              {intl.get('accountsPayable.upload.title')}
            </Typography>
          }
        />
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid sx={{ xs: 12 }}>
              <ErrorSummary errors={errors} showEverything={true} />
            </Grid>
            <Grid sx={{ xs: 12, width: '100%', maxWidth: 400 }}>
              <Autocomplete
                options={supplierOptions}
                getOptionLabel={(o) => o.name ?? ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                loading={supplierLoading}
                value={supplierValue}
                inputValue={supplierInputValue}
                onInputChange={(_e, value, reason) => {
                  if (reason === 'input') {
                    setSupplierInputValue(value);
                    searchSupplier(value);
                  } else if (reason === 'clear') {
                    setSupplierInputValue('');
                    setSupplierValue(null);
                  }
                }}
                onChange={(_e, value) => {
                  setSupplierValue(value);
                  setSupplierInputValue(value?.name ?? '');
                  setErrors((prev) => ({ ...prev, supplier: '' }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={intl.get('accountsPayable.upload.supplierLabel')}
                    error={Boolean(errors?.['supplier'])}
                    helperText={errors?.['supplier'] || undefined}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid sx={{ xs: 12, width: '100%', md: 5 }}>
              <InvoiceUploadDropZone />
            </Grid>
            <Grid sx={{ xs: 12, width: '100%', md: 0 }}>
              <Grid className="d-flex justify-content-end" sx={{ marginTop: '20px' }}>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1}
                  height="100%"
                  width="100%"
                >
                  <Button variant="outlined" color="inherit" onClick={() => navigate('/accountspayable')}>
                    {intl.get('workflow.action.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                    disabled={!input?.attachments?.[0] || !supplierValue || loading}
                  >
                    {intl.get('newInvoice.uploadInvoice')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

const APInvoiceUpload = () => {
  return (
    <InvoiceProvider review={false} view={false}>
      <APInvoiceUploadContent />
    </InvoiceProvider>
  );
};

export default APInvoiceUpload;
